import { useState, useEffect, createContext, useContext } from 'react';
import { Button } from '../ui/button';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetDescription,
  SheetTrigger
} from '../ui/sheet';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { 
  HelpCircle, Search, Play, Clock, CheckCircle, 
  ChevronRight, BookOpen, ShoppingCart, Store, Shield,
  ExternalLink, Star, Lightbulb, ArrowRight, X, Send,
  Loader2, Sparkles, MessageCircle
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getScreenContext, isEnglishOnlyPortal } from '../../data/tooltipData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Context for floating help button state
const FloatingHelpContext = createContext({
  isOpen: false,
  setIsOpen: () => {},
  currentContext: null,
  setCurrentContext: () => {}
});

export const useFloatingHelp = () => useContext(FloatingHelpContext);

// Priority tutorials data (Top 10)
const PRIORITY_TUTORIALS = {
  retailer: [
    {
      id: "RET-02",
      title: "Post a New Item",
      description: "List your surplus items for sale quickly",
      difficulty: "Medium",
      duration: "3 min",
      icon: "📦",
      steps: [
        "Click 'Post Item' or navigate to Scan page",
        "Scan barcode or enter item details manually",
        "Set original price and deal price (50-75% off)",
        "Enter quantity available",
        "Select category and expiry date",
        "Click 'Post Item' to publish"
      ],
      tips: [
        { element: "Deal Price", tip: "Must be 50-75% below original price for platform compliance" },
        { element: "Quantity", tip: "Update immediately when stock changes to avoid customer disappointment" }
      ],
      relatedPages: ["/retailer/scan", "/retailer/post-item"]
    },
    {
      id: "RET-06",
      title: "View Item Analytics",
      description: "Track your store's performance and identify top sellers",
      difficulty: "Medium",
      duration: "3 min",
      icon: "📊",
      steps: [
        "Navigate to Analytics from the menu",
        "View summary cards: Revenue, Orders, Conversion Rate",
        "Use date range selector to filter data",
        "Explore Sales Trends chart",
        "Check Top Items tab for best performers",
        "Export reports as CSV or PDF"
      ],
      tips: [
        { element: "Revenue", tip: "Total sales after platform fees are deducted" },
        { element: "Conversion Rate", tip: "Percentage of views that result in purchases" }
      ],
      relatedPages: ["/retailer/analytics"]
    }
    // NOTE: "Manage Inventory" removed - DealShaq auto-decrements; retailers post NEW RSHDs
  ],
  consumer: [
    {
      id: "CON-04",
      title: "Use Food Assistant",
      description: "Find deals using natural language queries",
      difficulty: "Medium",
      duration: "3 min",
      icon: "🤖",
      steps: [
        "Click Food Assistant in the dashboard",
        "Type a natural request like 'organic eggs under $5'",
        "Press Enter or click Send",
        "Review AI-matched results",
        "Click any item for details",
        "Add items directly to cart"
      ],
      tips: [
        { element: "Chat Input", tip: "Be specific! Try 'halal chicken near me' or 'gluten-free snacks'" },
        { element: "Memory", tip: "The AI learns your preferences over time for better recommendations" }
      ],
      relatedPages: ["/consumer/dashboard"]
    },
    {
      id: "CON-NLU-RESET",
      title: "Reset NLU Memory",
      description: "Clear your learned preferences and start fresh",
      difficulty: "Easy",
      duration: "1 min",
      icon: "🧠",
      steps: [
        "Open Food Assistant",
        "Click the 'Memory' button",
        "View your learned preferences",
        "Choose reset type: All, Short-term, or Long-term",
        "Confirm the reset",
        "Start fresh with new preferences"
      ],
      tips: [
        { element: "Short-term", tip: "Clears recent conversation context only" },
        { element: "Long-term", tip: "Clears all learned dietary preferences and budget patterns" }
      ],
      relatedPages: ["/consumer/dashboard"]
    },
    {
      id: "CON-06",
      title: "Add Item to Cart",
      description: "Complete your purchase smoothly",
      difficulty: "Easy",
      duration: "2 min",
      icon: "🛒",
      steps: [
        "Find a deal you want to purchase",
        "Click 'Add to Cart' button",
        "Continue shopping or proceed to checkout",
        "Review cart items and quantities",
        "Select pickup or delivery",
        "Complete payment with Stripe"
      ],
      tips: [
        { element: "Pickup", tip: "Usually ready in 2-4 hours at the store" },
        { element: "Cart", tip: "Items from different stores will be grouped separately" }
      ],
      relatedPages: ["/consumer/browse", "/consumer/checkout"]
    }
  ],
  admin: [
    {
      id: "ADM-03",
      title: "Approve Retailer or Brand",
      description: "Review and activate new retailer applications",
      difficulty: "Medium",
      duration: "3 min",
      icon: "✅",
      steps: [
        "Navigate to Approvals page",
        "View pending retailer applications",
        "Click on a retailer to review details",
        "Verify business information and documents",
        "Check store location on map",
        "Click Approve or Reject with reason"
      ],
      tips: [
        { element: "Approve", tip: "Retailer will immediately be able to post deals" },
        { element: "Reject", tip: "Always provide a clear reason to help them reapply" }
      ],
      relatedPages: ["/admin/approvals"]
    },
    {
      id: "ADM-04",
      title: "Boost or Suspend Item",
      description: "Control item visibility across the platform",
      difficulty: "Medium",
      duration: "3 min",
      icon: "⚡",
      steps: [
        "Navigate to Curation or Items page",
        "Search for the item to modify",
        "To boost: Click 'Feature a Deal'",
        "Set priority level and duration",
        "To suspend: Click item and select 'Suspend'",
        "Changes take effect immediately"
      ],
      tips: [
        { element: "Priority", tip: "Higher priority = more prominent homepage placement" },
        { element: "Suspend", tip: "Use for policy violations or quality issues" }
      ],
      relatedPages: ["/admin/curation", "/admin/items"]
    },
    {
      id: "ADM-05",
      title: "View Platform Analytics",
      description: "Monitor overall platform health and growth",
      difficulty: "Medium",
      duration: "3 min",
      icon: "📈",
      steps: [
        "Navigate to Analytics dashboard",
        "Review platform-wide metrics",
        "Check user growth trends",
        "Analyze demand heatmap by category",
        "View top performing retailers",
        "Export reports for stakeholders"
      ],
      tips: [
        { element: "User Growth", tip: "Track new registrations by consumer vs retailer" },
        { element: "Demand Heatmap", tip: "Identify which categories need more inventory" }
      ],
      relatedPages: ["/admin/analytics"]
    }
  ],
  all: [
    {
      id: "ALL-WALLET",
      title: "View Wallet & Billing",
      description: "Manage your account balance and payment history",
      difficulty: "Easy",
      duration: "2 min",
      icon: "💰",
      steps: [
        "Navigate to Settings or Account page",
        "Find the Wallet/Billing section",
        "View current balance and credits",
        "Review transaction history",
        "Add payment method if needed",
        "Set up auto-reload (optional)"
      ],
      tips: [
        { element: "Balance", tip: "Credits are used for platform features and promotions" },
        { element: "History", tip: "Download statements for your records" }
      ],
      relatedPages: ["/consumer/settings", "/retailer/dashboard"]
    }
  ]
};

// Quick tips for contextual help
const QUICK_TIPS = {
  "/consumer/browse": [
    { icon: "🔍", tip: "Use filters to narrow down by category, distance, or discount level" },
    { icon: "⚡", tip: "Green 'Live' indicator means real-time inventory updates are active" },
    { icon: "❤️", tip: "Click the heart to add items to your favorites for alerts" }
  ],
  "/consumer/favorites": [
    { icon: "🔔", tip: "Set a discount threshold to only get notified for big savings" },
    { icon: "💰", tip: "Set a max price to filter out items above your budget" }
  ],
  "/consumer/dashboard": [
    { icon: "🤖", tip: "Try the Food Assistant for natural language searches" },
    { icon: "📍", tip: "Set your location in Settings for accurate deal distances" }
  ],
  "/retailer/scan": [
    { icon: "📷", tip: "Hold camera steady and ensure good lighting for best results" },
    { icon: "🔢", tip: "PLU codes on produce stickers start with 4 (conventional) or 9 (organic)" },
    { icon: "📦", tip: "Use Batch mode to scan multiple items at once" }
  ],
  "/retailer/inventory": [
    { icon: "⏰", tip: "Items close to expiry should be priced at 75% off for faster sales" },
    { icon: "📊", tip: "Track which items sell fastest in Analytics" }
  ],
  "/admin/approvals": [
    { icon: "✅", tip: "Verify business license and food handling permits before approval" },
    { icon: "📍", tip: "Check that store location matches business registration" }
  ]
};

// FloatingHelpButton component
export function FloatingHelpButton({ userRole = 'consumer' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [activeTab, setActiveTab] = useState('tutorials'); // 'tutorials' | 'ai'
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Get current path for contextual tips
    setCurrentPath(location.pathname);
  }, [isOpen, location]);

  // Get screen context for AI Agent
  const screenContext = getScreenContext(currentPath);
  const isEnglishOnly = isEnglishOnlyPortal(screenContext.portal);

  // Handle AI Help query with context
  const handleAIQuery = async () => {
    if (!aiQuery.trim()) return;
    
    setAiLoading(true);
    setAiResponse('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Build context-aware message
      const contextMessage = `[Context: ${screenContext.portal} portal, ${screenContext.primaryTopic} screen. Priority intents: ${screenContext.priorityIntents?.join(', ')}. Context hints: ${screenContext.contextHints?.join(', ')}]

User question: ${aiQuery}`;
      
      const response = await fetch(`${API_URL}/api/agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          message: contextMessage,
          session_id: `help-${Date.now()}`,
          context: {
            portal: screenContext.portal,
            screen: screenContext.primaryTopic,
            language_policy: isEnglishOnly ? 'english_only' : 'multilingual'
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiResponse(data.response || 'I can help you with that. Please try rephrasing your question.');
      } else {
        setAiResponse(isEnglishOnly 
          ? 'Unable to connect to the AI assistant. Please try searching the tutorials or visit the Documentation Hub.'
          : 'Unable to connect. Please try the tutorials or Documentation Hub.');
      }
    } catch (error) {
      console.error('AI help error:', error);
      setAiResponse('Something went wrong. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const trackTutorialView = async (tutorialId) => {
    try {
      await fetch(`${API_URL}/api/tutorials/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tutorial_id: tutorialId,
          action: 'view',
          context: currentPath
        })
      });
    } catch (e) {
      console.log('Tutorial tracking failed:', e);
    }
  };

  // Navigate to Documentation Hub with context
  const handleDocHubLink = () => {
    const docPath = `/${screenContext.portal}/help`;
    if (screenContext.relatedDocs?.length > 0) {
      navigate(`${docPath}?doc=${screenContext.relatedDocs[0]}`);
    } else {
      navigate(docPath);
    }
    setIsOpen(false);
  };

  // Get tutorials for current role + "all"
  const relevantTutorials = [
    ...(PRIORITY_TUTORIALS[userRole] || []),
    ...PRIORITY_TUTORIALS.all
  ];

  const filteredTutorials = searchQuery
    ? relevantTutorials.filter(t =>
        t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : relevantTutorials;

  const contextualTips = QUICK_TIPS[currentPath] || [];

  const getDifficultyColor = (diff) => {
    switch (diff) {
      case 'Easy': return 'bg-green-100 text-green-700';
      case 'Medium': return 'bg-yellow-100 text-yellow-700';
      case 'Hard': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          className="fixed bottom-20 right-6 w-14 h-14 rounded-full shadow-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 z-50"
          data-testid="floating-help-btn"
        >
          <HelpCircle className="w-6 h-6 text-white" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg" data-testid="help-panel">
        <SheetHeader className="border-b pb-4">
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-600" />
            Help Center
          </SheetTitle>
          <SheetDescription>
            {screenContext.primaryTopic !== 'general' && (
              <Badge variant="outline" className="mr-2">
                {screenContext.primaryTopic}
              </Badge>
            )}
            {userRole === 'consumer' ? 'Shoppers' : userRole === 'retailer' ? 'Retailers' : userRole === 'admin' ? 'Admins' : 'Charities'}
          </SheetDescription>
        </SheetHeader>

        <div className="py-4">
          {/* Tab Switcher */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={activeTab === 'tutorials' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('tutorials')}
              className="flex-1"
            >
              <Star className="w-4 h-4 mr-1" />
              Tutorials
            </Button>
            <Button
              variant={activeTab === 'ai' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('ai')}
              className="flex-1"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              AI Help
            </Button>
          </div>

          {activeTab === 'tutorials' ? (
            <>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search tutorials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="help-search-input"
                />
              </div>

              <ScrollArea className="h-[calc(100vh-340px)]">
                {/* Contextual Tips */}
                {contextualTips.length > 0 && !searchQuery && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-yellow-500" />
                      Tips for this page
                    </h3>
                    <div className="space-y-2">
                      {contextualTips.map((tip, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg text-sm">
                          <span>{tip.icon}</span>
                          <span className="text-gray-700">{tip.tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tutorials */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-emerald-500" />
                    {searchQuery ? 'Search Results' : 'Priority Tutorials'}
                  </h3>
                  <div className="space-y-3">
                    {filteredTutorials.map((tutorial) => (
                      <TutorialCard
                        key={tutorial.id}
                        tutorial={tutorial}
                        onView={() => trackTutorialView(tutorial.id)}
                        getDifficultyColor={getDifficultyColor}
                      />
                    ))}
                    {filteredTutorials.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p>No tutorials found for "{searchQuery}"</p>
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </>
          ) : (
            /* AI Help Tab */
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-emerald-900 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Ask About: {screenContext.primaryTopic}
                </h3>
                <p className="text-xs text-emerald-700">
                  {screenContext.contextHints?.slice(0, 3).join(' • ')}
                </p>
                {isEnglishOnly && (
                  <Badge variant="outline" className="mt-2 text-xs">
                    English only
                  </Badge>
                )}
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder={isEnglishOnly ? "Ask a question..." : "Ask in any language..."}
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAIQuery()}
                  data-testid="ai-help-input"
                />
                <Button 
                  onClick={handleAIQuery} 
                  disabled={aiLoading || !aiQuery.trim()}
                  size="icon"
                >
                  {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>

              {/* Suggested Questions */}
              <div className="flex flex-wrap gap-2">
                {screenContext.contextHints?.slice(0, 4).map((hint, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="cursor-pointer hover:bg-gray-100 text-xs"
                    onClick={() => setAiQuery(`How do I ${hint}?`)}
                  >
                    {hint}
                  </Badge>
                ))}
              </div>

              {/* AI Response */}
              {aiResponse && (
                <ScrollArea className="h-[calc(100vh-500px)]">
                  <div className="bg-white border rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{aiResponse}</p>
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              )}
            </div>
          )}

          {/* Help Links - Always visible */}
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={handleDocHubLink}
              className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="full-help-center-link"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <BookOpen className="w-4 h-4" />
                View Full Documentation Hub
              </span>
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Tutorial Card component with expandable details
function TutorialCard({ tutorial, onView, getDifficultyColor }) {
  const [expanded, setExpanded] = useState(false);

  const handleExpand = () => {
    if (!expanded) {
      onView();
    }
    setExpanded(!expanded);
  };

  return (
    <div
      className={`border rounded-lg transition-all ${expanded ? 'ring-2 ring-emerald-500 bg-white' : 'bg-white hover:shadow-md cursor-pointer'}`}
      data-testid={`tutorial-card-${tutorial.id}`}
    >
      <div 
        className="p-4 flex items-start justify-between"
        onClick={handleExpand}
      >
        <div className="flex items-start gap-3">
          <span className="text-2xl">{tutorial.icon}</span>
          <div>
            <h4 className="font-medium text-gray-900">{tutorial.title}</h4>
            <p className="text-sm text-gray-500 mt-0.5">{tutorial.description}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={getDifficultyColor(tutorial.difficulty)}>
                {tutorial.difficulty}
              </Badge>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {tutorial.duration}
              </span>
            </div>
          </div>
        </div>
        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t pt-4">
          {/* Steps */}
          <div className="mb-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              Steps
            </h5>
            <ol className="space-y-2">
              {tutorial.steps.map((step, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-medium">
                    {idx + 1}
                  </span>
                  <span className="text-gray-700">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Tips */}
          <div className="bg-blue-50 rounded-lg p-3">
            <h5 className="text-sm font-semibold text-blue-900 mb-2">💡 Tips</h5>
            <ul className="space-y-1">
              {tutorial.tips.map((tip, idx) => (
                <li key={idx} className="text-sm text-blue-800">
                  <strong>{tip.element}:</strong> {tip.tip}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// Provider for floating help context
export function FloatingHelpProvider({ children, userRole }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentContext, setCurrentContext] = useState(null);

  return (
    <FloatingHelpContext.Provider value={{ isOpen, setIsOpen, currentContext, setCurrentContext }}>
      {children}
      <FloatingHelpButton userRole={userRole} />
    </FloatingHelpContext.Provider>
  );
}

export default FloatingHelpButton;
